import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Check, Loader2, RotateCcw, Sparkles } from 'lucide-react'
import { Page } from '@/components/Page'
import { Modal } from '@/components/Modal'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { formatDateTime } from '@/lib/locale'
import {
  useBillingSubscription,
  useCancelSubscription,
  useCreateCheckoutSession,
  useCurrentTier,
  useResumeSubscription,
} from '@/features/subscriptions/queries'
import type { PaidPlanId, PlanId } from '@/features/subscriptions/types'

type Plan = {
  id: PlanId
  name: string
  price: string
  cadence?: string
  desc: string
  features: string[]
  highlighted?: boolean
}

const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    cadence: '/mo',
    desc: 'For individuals & testing.',
    features: [
      '1 Million AI Tokens',
      '500 Transactions/mo',
      'Standard Processing',
      'Basic Reports',
      'Unlimited free team invitations',
    ],
  },
  {
    id: 'basic',
    name: 'Basic',
    price: '$100',
    cadence: '/mo',
    desc: 'For growing small businesses.',
    features: [
      '10 Million AI Tokens',
      '5,000 Transactions/mo',
      'Standard Email Support',
      'Basic Financial Reports',
      'Bank Statement Processing',
      'Unlimited free team invitations',
    ],
  },
  {
    id: 'standard',
    name: 'Standard',
    price: '$200',
    cadence: '/mo',
    desc: 'For established companies.',
    features: [
      '50 Million AI Tokens',
      '15,000 Transactions/mo',
      'Priority Email Support',
      'Advanced Analytics & Forecasting',
      'Automated Reconciliation',
      'Unlimited free team invitations',
    ],
    highlighted: true,
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '$400',
    cadence: '/mo',
    desc: 'For high-volume enterprises.',
    features: [
      'Unlimited AI Tokens',
      'Unlimited Transactions',
      '24/7 Dedicated Support',
      'Custom Reporting Engine',
      'Multi-User Access & Roles',
      'API Access for ERP Integration',
      'Quarterly Strategy Review',
      'Unlimited free team invitations',
    ],
  },
]

export function SubscriptionsPage() {
  const { toast } = useToast()
  const tierQ = useCurrentTier()
  const subQ = useBillingSubscription()
  const checkoutM = useCreateCheckoutSession()
  const cancelM = useCancelSubscription()
  const resumeM = useResumeSubscription()
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)

  const tier = tierQ.data ?? 'free'
  const sub = subQ.data?.subscription
  const isPaidTier = tier !== 'free'
  const canCancel = isPaidTier && !!sub && !sub.cancelAtPeriodEnd
  const isCanceling = isPaidTier && !!sub && sub.cancelAtPeriodEnd

  const onCancel = async () => {
    try {
      const r = await cancelM.mutateAsync()
      setConfirmCancelOpen(false)
      toast({
        title: 'Subscription canceled',
        description: r.subscription?.currentPeriodEnd
          ? `You'll keep access until ${formatDateTime(r.subscription.currentPeriodEnd)}.`
          : 'Your subscription will not renew.',
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not cancel',
        description: messageFor(err, 'Try again in a moment.'),
      })
    }
  }

  const onResume = async () => {
    try {
      await resumeM.mutateAsync()
      toast({ title: 'Subscription resumed', description: 'Auto-renewal is back on.' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not resume',
        description: messageFor(err, 'Try again in a moment.'),
      })
    }
  }

  const onSelect = async (planId: PaidPlanId) => {
    try {
      const res = await checkoutM.mutateAsync(planId)
      if (res.url) {
        window.location.assign(res.url)
      } else {
        throw new Error(res.error ?? 'Checkout URL missing.')
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Unable to start checkout',
        description: messageFor(err, 'Connection failed. Please check your internet.'),
      })
    }
  }

  const busyPlan = checkoutM.isPending ? checkoutM.variables : null

  return (
    <Page
      title="Upgrade Plan"
      description="Choose the plan that fits your team. All prices in USD."
      width="wide"
    >
      {isCanceling ? (
        <Alert variant="warning">
          <AlertTriangle className="size-4" />
          <AlertTitle>Subscription canceling at period end</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>
              You&apos;ll keep full access until{' '}
              <strong>{sub?.currentPeriodEnd ? formatDateTime(sub.currentPeriodEnd) : 'the end of your current period'}</strong>.
              Want to keep your plan? You can turn auto-renewal back on.
            </span>
            <Button
              size="sm"
              variant="secondary"
              onClick={onResume}
              disabled={resumeM.isPending}
            >
              <RotateCcw className="size-4" />
              Resume subscription
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {canCancel ? (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
            <div>
              <h3 className="text-sm font-semibold">Current subscription</h3>
              <p className="text-sm text-muted-foreground">
                You&apos;re on the <strong className="capitalize">{tier}</strong> plan.
                {sub?.currentPeriodEnd ? (
                  <> Renews on <strong>{formatDateTime(sub.currentPeriodEnd)}</strong>.</>
                ) : null}
              </p>
            </div>
            <Button
              variant="danger"
              onClick={() => setConfirmCancelOpen(true)}
              disabled={cancelM.isPending}
            >
              Cancel subscription
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <Modal.Confirm
        open={confirmCancelOpen}
        onOpenChange={setConfirmCancelOpen}
        title="Cancel subscription?"
        body={
          <p>
            You&apos;ll keep full access until{' '}
            <strong>
              {sub?.currentPeriodEnd ? formatDateTime(sub.currentPeriodEnd) : 'the end of your current period'}
            </strong>
            . You can resume any time before then to stay on the plan.
          </p>
        }
        confirmLabel="Cancel subscription"
        cancelLabel="Keep plan"
        confirmVariant="danger"
        onConfirm={onCancel}
        busy={cancelM.isPending}
      />

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {PLANS.map((plan) => {
          const isCurrent = plan.id === tier
          const isPaid = plan.id !== 'free'
          const isBusy = busyPlan === plan.id
          return (
            <Card
              key={plan.id}
              className={cn(
                'relative flex flex-col',
                plan.highlighted && !isCurrent && 'border-primary shadow-lg',
                isCurrent && 'border-2 border-success',
              )}
            >
              {plan.highlighted ? (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-pill bg-primary px-3 py-1 text-[10px] uppercase tracking-wider text-primary-foreground">
                  <Sparkles className="mr-1 size-3" />
                  Most Popular
                </Badge>
              ) : null}
              <CardHeader>
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <div className="mt-1 text-3xl font-bold text-foreground">
                  {plan.price}
                  {plan.cadence ? (
                    <span className="ml-1 text-sm font-normal text-muted-foreground">
                      {plan.cadence}
                    </span>
                  ) : null}
                </div>
                <p className="text-sm text-muted-foreground">{plan.desc}</p>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col justify-between gap-6">
                <ul className="space-y-2 text-sm">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="mt-0.5 size-4 shrink-0 text-success" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <Button className="w-full bg-success text-success-foreground hover:bg-success/90" disabled>
                    Current Plan
                  </Button>
                ) : isPaid ? (
                  <Button
                    className="w-full"
                    variant={plan.highlighted ? 'primary' : 'secondary'}
                    onClick={() => onSelect(plan.id as PaidPlanId)}
                    disabled={checkoutM.isPending}
                  >
                    {isBusy ? <Loader2 className="size-4 animate-spin" /> : null}
                    {isBusy ? 'Processing…' : `Select ${plan.name}`}
                  </Button>
                ) : (
                  <Button className="w-full" variant="secondary" disabled>
                    Select {plan.name}
                  </Button>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

      <p className="text-center text-sm text-muted-foreground">
        Need a custom enterprise solution?{' '}
        <Link to="/contact-sales" className="font-medium text-primary hover:underline">
          Contact us
        </Link>
      </p>
    </Page>
  )
}

function messageFor(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}
